import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { Balance } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Unit Of Measures';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    uoms: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    setDataIsFetching(true);

    let requestUrl = `/unit-of-measures?per_page=${perPage}&page=${page}`;

    if (statusFilter === 'ACTIVE') {
      requestUrl += '&is_active=true';
      setAppliedFilters(['is_active']);
    } else if (statusFilter === 'INACTIVE') {
      requestUrl += '&is_active=false';
      setAppliedFilters(['is_active']);
    } else {
      setAppliedFilters([]);
    }
    console.log(requestUrl);
    getUOMS(requestUrl);
  }, [statusFilter, perPage, page]);

  function getUOMS(requestUrl) {
    // make the request
    userRequest()
      .get(requestUrl)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          uoms: data.unitOfMeasures,
        });
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }

  function setStatus(id, data) {
    userRequest()
      .post(`/unit-of-measures/update/${id}`, data)
      .then(() => {
        let uoms = [];
        if (meetFilterConditions(data)) {
          uoms = tableData.uoms.map((uom) => {
            if (id === uom.id) uom.is_active = !uom.is_active;
            return uom;
          });
        } else {
          uoms = tableData.uoms.filter((u) => u.id !== id);
        }
        setTableData({
          count: uoms.length,
          uoms,
        });
      })
      .catch(() => {});
  }

  function getFilters() {
    if (statusFilter === 'ACTIVE') {
      return [`Status:Active `];
    } else if (statusFilter === 'INACTIVE') {
      return [`Status:Inactive `];
    } else if (statusFilter === 'All') {
      return [];
    }
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (statusFilter === 'ALL') {
      return true;
    }
    if (appliedFilters.length > 0) return statusFilter === data.is_active;

    return true;
  }

  const onSubmit = (data) => {
    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });

      let api;
      if (modalState.update) {
        api = `/unit-of-measures/update/${modalState.update}`;
      } else {
        api = '/unit-of-measures';
      }

      userRequest()
        .post(api, data)
        .then((res) => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });

          //Refresh Table
          getUOMS(`/unit-of-measures?per_page=${perPage}&page=${page}`);

          reset(defaultValues);
          setModalState({ update: true, open: false });
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };

  const columns = [
    {
      field: 'display_name',
      headerName: 'UOM (Name)',
      flex: 1,
    },
    {
      field: 'display_unit',
      headerName: 'UOM (Display Unit)',
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      flex: 1,
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<Balance />} title="Manage" parent="Unit Of Measures" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
              <Select
                name="Active"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={'ACTIVE'}>Active</MenuItem>
                <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ open: true, update: false });
          reset(defaultValues);
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      {/* CREATE UOM MODAL */}
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ open: false, update: false });
          reset(defaultValues);
          console.log('Button has been reset:', watch());
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ open: false, update: false });
            reset(defaultValues);
          }}
        >
          {modalState.update ? 'Update' : 'Create'} Unit Of Measure
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <TextField
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                  label="Name"
                  variant="outlined"
                  // helperText={errors.display_name?.message}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group row">
                <TextField
                  label="Display Unit"
                  variant="outlined"
                  {...register(`display_unit`)}
                  error={!!(errors.display_unit?.message && isSubmitted && getValues().display_unit === '')}
                  // helperText={errors.display_unit?.message}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="d-flex justify-content-start my-4">
                {modalState.update && (
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                )}
              </div>
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.uoms}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

const defaultValues = {
  display_name: '',
  display_unit: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  display_name: Yup.string().required('Required'),
  display_unit: Yup.string().required('Required'),
  is_active: Yup.boolean().required(),
});

export default Manage;
